import React, { useEffect, useRef, useState } from 'react';
import { compareVersions } from '../utils/version';

export interface AgentData {
  // --- Always static ---
  id: string;
  icon: React.ReactNode;
  // --- Live or static ---
  kpis?: { label: string; value: string }[];
  updates?: {
    type: 'Incoming' | 'Update' | 'New';
    version: string;
    title: string;
    description: string;
    date?: string;
  }[];
  url?: string;
  liveMetadata?: boolean;
  iconBackground?: string;
  iconModalScale?: string;
  // --- Live (from /api/v1/metadata) or static fallback ---
  title?: string;
  description?: string;
  fullDescription?: string;
  tags?: string[];
  status?: string;
  coreCapabilities?: {
    name: string;
    description: string;
    icon: string;
  }[];
  owners?: {
    agentOwner: string;
    businessOwner: string;
    supportContact: string;
    version: string | null;
  };
  faqs?: {
    question: string;
    answer: string;
  }[];
  knowledgeSources?: { name: string; url: string }[];
  metadataError?: boolean;
}

interface AgentModalProps {
  agent: AgentData;
  onClose: () => void;
  isLocked?: boolean;
  initialSection?: 'updates' | null;
  seenVersion?: string;
}

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ChevronDown = ({ className = '' }: { className?: string }) => (
  <svg className={`w-5 h-5 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const AgentModal: React.FC<AgentModalProps> = ({
  agent,
  onClose,
  isLocked,
  initialSection,
  seenVersion,
}) => {
  // Prevent scrolling on body when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const whatsNewRef = useRef<HTMLDivElement>(null);

  // When opened via the card's "new" badge, scroll straight to What's new.
  useEffect(() => {
    if (initialSection !== 'updates') return;
    const id = window.setTimeout(() => {
      whatsNewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => {
      window.clearTimeout(id);
    };
  }, [initialSection, agent.id]);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Indus':
        return 'bg-emerald-500 text-white';
      case 'Scale':
        return 'bg-emerald-500 text-white';
      case 'POC':
        return 'bg-amber-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      {/* Added mt-20 to push below header, reduced max-w and max-h */}
      <div className="relative bg-white w-full max-w-4xl max-h-[85vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up mt-20 border border-gray-100">
        {/* Modal Header Actions */}
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={onClose}
            className="p-2 bg-white/80 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-black backdrop-blur-sm border border-gray-100"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* 1. Header Section */}
          <div className="p-8 md:p-10 pb-6">
            <div className="flex flex-col-reverse md:flex-row justify-between items-start gap-8">
              <div className="flex-1">
                <div className="mb-3">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-2">
                    {agent.title ?? ''}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2">
                    {agent.status && (
                      <span
                        className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${getStatusColor(agent.status)}`}
                      >
                        {agent.status}
                      </span>
                    )}
                    {isLocked && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-red-100 border border-red-200 text-red-600">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        Access restricted
                      </span>
                    )}
                  </div>
                </div>

                {/* KPIs */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {(agent.kpis ?? []).map((kpi, idx) => (
                    <div
                      key={idx}
                      className="flex-1 min-w-[120px] bg-white border border-gray-200 rounded-xl p-3 shadow-sm text-center"
                    >
                      <div className="text-xl font-bold text-gray-900">{kpi.value}</div>
                      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-1">
                        {kpi.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Icon Box */}
              <div className="relative flex-shrink-0">
                <div
                  className={`w-28 h-28 md:w-40 md:h-40 rounded-3xl flex items-center justify-center shadow-lg ${agent.iconBackground ?? 'bg-[#81D8D0]/20 border border-[#81D8D0]/30'}`}
                >
                  <div
                    className={`${agent.iconModalScale ?? 'scale-[2]'} transform text-[#81D8D0]`}
                  >
                    {React.isValidElement(agent.icon)
                      ? React.cloneElement(
                          agent.icon as React.ReactElement<{ className?: string }>,
                          { className: '' }
                        )
                      : agent.icon}
                  </div>
                </div>
                <div className="absolute top-3 right-3 w-3 h-3 bg-white rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-[#10B981] rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          {agent.metadataError ? (
            /* Error state: replaces metadata strip + main content */
            <div className="flex flex-col items-center justify-center px-8 pt-8 pb-16 gap-4">
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="28" cy="28" r="26" stroke="#D1D5DB" strokeWidth="2" fill="#F9FAFB" />
                <circle cx="20" cy="23" r="2.5" fill="#9CA3AF" />
                <circle cx="36" cy="23" r="2.5" fill="#9CA3AF" />
                <path
                  d="M20 37 Q28 31 36 37"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
              <p className="text-base text-gray-400">Unable to load agent information.</p>
            </div>
          ) : (
            <>
              {/* 2. Metadata Strip */}
              <div className="bg-gray-50 border-y border-gray-100 px-8 py-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Agent Owner
                    </div>
                    <div className="font-medium text-sm text-gray-900">
                      {agent.owners?.agentOwner}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Business Owner
                    </div>
                    <div className="font-medium text-sm text-gray-900">
                      {agent.owners?.businessOwner}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Support Contact
                    </div>
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {agent.owners?.supportContact}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Current Version
                    </div>
                    <div className="font-medium text-sm text-gray-900">{agent.owners?.version}</div>
                  </div>
                </div>
              </div>

              {/* 3. Main Content with Vertical Line */}
              <div className="relative px-8 md:px-12 py-10">
                {/* The Vertical Line */}
                <div className="absolute left-[3.5rem] md:left-[4.5rem] top-10 bottom-10 w-0.5 bg-gradient-to-b from-[#5B4DFF] via-gray-200 to-gray-100"></div>

                {/* Section: About */}
                <div className="relative pl-12 md:pl-16 mb-12">
                  <div className="absolute left-[-5px] md:left-[17px] top-1 w-4 h-4 rounded-full border-4 border-white bg-[#5B4DFF] shadow-sm z-10"></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">About this agent</h3>
                  <div className="prose prose-sm text-gray-600 max-w-3xl">
                    <p>{agent.fullDescription}</p>
                    {(agent.coreCapabilities?.length ?? 0) > 0 && (
                      <div className="mt-5 p-5 bg-blue-50/50 rounded-2xl border border-blue-100">
                        <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wide mb-3">
                          Core Capabilities
                        </h4>
                        <div className="grid sm:grid-cols-3 gap-3">
                          {agent.coreCapabilities!.map((capability) => (
                            <div
                              key={capability.name}
                              className="bg-white p-3 rounded-xl border border-blue-100 shadow-sm text-center"
                            >
                              <div className="text-xl mb-1">{capability.icon}</div>
                              <div className="font-semibold text-sm text-gray-900">
                                {capability.name}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {capability.description}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section: What's New */}
                {(agent.updates?.length ?? 0) > 0 && (
                  <div ref={whatsNewRef} className="relative pl-12 md:pl-16 mb-12">
                    <div className="absolute left-[-5px] md:left-[17px] top-1 w-4 h-4 rounded-full border-4 border-white bg-[#5B4DFF] shadow-sm z-10"></div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">What&apos;s new</h3>

                    <div className="space-y-4">
                      {agent.updates!.map((update, idx) => {
                        const isNew =
                          update.type !== 'Incoming' &&
                          compareVersions(update.version, seenVersion) > 0;
                        return (
                          <div
                            key={idx}
                            className={`border rounded-2xl p-5 transition-all relative overflow-hidden ${
                              isNew
                                ? 'bg-[#81D8D0]/10 border-[#81D8D0] shadow-lg shadow-[#81D8D0]/20 ring-1 ring-[#81D8D0]/40 pl-6'
                                : 'bg-white border-gray-100 shadow-sm hover:shadow-md'
                            }`}
                          >
                            {isNew && (
                              <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#81D8D0]" />
                            )}
                            {/* Connection dot to main line */}
                            <div
                              className={`absolute left-[-2rem] top-6 w-2 h-2 rounded-full ${
                                isNew
                                  ? 'bg-[#81D8D0] animate-pulse ring-4 ring-[#81D8D0]/25'
                                  : 'bg-gray-300'
                              }`}
                            ></div>

                            <div className="flex flex-col sm:flex-row gap-3 items-start">
                              <div className="flex-shrink-0">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide
                              ${
                                update.type === 'Incoming'
                                  ? 'bg-gray-100 text-gray-800'
                                  : update.type === 'Update'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                              }`}
                                >
                                  {update.type}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between items-start">
                                  <h4 className="text-base font-bold text-gray-900">
                                    {update.title}
                                    {isNew && (
                                      <span className="ml-2 inline-flex items-center align-middle rounded-full bg-[#81D8D0] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                                        New
                                      </span>
                                    )}
                                  </h4>
                                  {update.date && (
                                    <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                                      {update.date}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 text-sm text-gray-600">{update.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Section: Sources */}
                {(agent.knowledgeSources?.length ?? 0) > 0 && (
                  <div className="relative pl-12 md:pl-16 mb-12">
                    <div className="absolute left-[-5px] md:left-[17px] top-1 w-4 h-4 rounded-full border-4 border-white bg-[#81D8D0] shadow-sm z-10"></div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Sources</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                      {agent.knowledgeSources!.map((source, idx) => (
                        <a
                          key={idx}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm hover:shadow-md hover:border-[#81D8D0] transition-all group no-underline"
                        >
                          <svg
                            className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-[#81D8D0]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5"
                            />
                          </svg>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 break-words">
                            {source.name}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Section: FAQ */}
                {(agent.faqs?.length ?? 0) > 0 && (
                  <div className="relative pl-12 md:pl-16">
                    <div className="absolute left-[-5px] md:left-[17px] top-1 w-4 h-4 rounded-full border-4 border-white bg-[#E93D82] shadow-sm z-10"></div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">
                      Everything you need to know
                    </h3>

                    <div className="grid md:grid-cols-2 gap-3 items-start">
                      {agent.faqs!.map((faq, idx) => (
                        <div
                          key={idx}
                          className={`border rounded-xl transition-all duration-200 bg-white
                       ${openFaq === idx ? 'border-[#5B4DFF] shadow-md ring-1 ring-[#5B4DFF]/20' : 'border-gray-200 hover:border-gray-300'}
                     `}
                        >
                          <button
                            onClick={() => toggleFaq(idx)}
                            className="w-full text-left px-5 py-4 flex justify-between items-center focus:outline-none"
                          >
                            <span className="font-semibold text-sm text-gray-900">
                              {faq.question}
                            </span>
                            <ChevronDown
                              className={`transform transition-transform duration-200 text-gray-400 ${openFaq === idx ? 'rotate-180 text-[#5B4DFF]' : ''}`}
                            />
                          </button>
                          <div
                            className={`px-5 overflow-hidden transition-all duration-300 ease-in-out
                         ${openFaq === idx ? 'max-h-48 pb-5 opacity-100' : 'max-h-0 opacity-0'}
                       `}
                          >
                            <p className="text-gray-600 text-xs leading-relaxed border-t border-gray-50 pt-3">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentModal;
